import React, { useMemo, useRef, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FCWithFragments } from '@modernloop/shared/components';
import { CanDisable } from '@modernloop/shared/helper-components';
import { BreakIcon, OrderLockIcon, OvernightIcon, TrashIcon, TriangleDownIcon } from '@modernloop/shared/icons';
import { Button, IconButton, Paper } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';

import {
  BreakSlot_BreakSlotFragment,
  BreakSlot_JobStageInterviewGroupsFragment,
  InterviewType,
} from 'src/generated/mloop-graphql';

import DurationMenu, { getDurationLabel } from 'src/components/DurationMenu';

import { isBreak, isDayBreak } from 'src/utils/interview';

import MoreMenuOptions, { InterviewMenuOptions } from './MoreMenuOptions';

type Props = {
  index: number;
  readonly?: boolean;
  isOnlyInterviewInGroup: boolean;
  onUpdated: (breakSlot: BreakSlot_BreakSlotFragment) => void;
  onDelete: (jobStageInterviewId: string) => void;
  onMenuSelect: (option: InterviewMenuOptions) => void;
};

type Fragments = {
  breakSlot: BreakSlot_BreakSlotFragment;
  jobStageInterviewGroups: BreakSlot_JobStageInterviewGroupsFragment[];
};

const BreakSlot: FCWithFragments<Fragments, Props> = ({
  breakSlot,
  jobStageInterviewGroups,
  index,
  readonly,
  isOnlyInterviewInGroup,
  onUpdated,
  onDelete,
  onMenuSelect,
}): JSX.Element => {
  const { t } = useTranslation();
  const anchorEl = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  const interviewSlotDuration = breakSlot.duration;
  const durationString = useMemo(() => {
    let result = interviewSlotDuration != null ? getDurationLabel(interviewSlotDuration) : undefined;
    if (!result && interviewSlotDuration) {
      result = t('hiring-dashboard-common:common.plurals.minCount', { count: interviewSlotDuration });
    }

    return result;
  }, [interviewSlotDuration, t]);

  const openBreakDurationMenu = () => {
    setOpen(true);
  };

  const closeBreakDurationMenu = () => {
    setOpen(false);
  };

  const handleDurationSelect = (duration: number) => {
    const interviewType = duration === 0 ? InterviewType.DayDivider : InterviewType.Break;
    onUpdated({ ...breakSlot, duration, interviewType, name: duration === 0 ? 'Day Divider' : 'Break' });
  };

  const handleDeleteBreakSlot = () => {
    onDelete(breakSlot.id);
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        backgroundColor: 'background.contrast',
        padding: 1.5,
        borderRadius: isOnlyInterviewInGroup ? undefined : 1.5,
      }}
    >
      <CanDisable disabled={Boolean(readonly)}>
        <Grid container alignItems="center" justifyContent="space-between" wrap="nowrap">
          <Grid xs={10}>
            <Grid container alignItems="center" spacing={1}>
              <Grid>
                <Grid container alignItems="center" spacing={1}>
                  <Grid>
                    <OrderLockIcon tooltip={t('hiring-dashboard-schedule:schedule.interviewPlan.lockedPosition')} />
                  </Grid>

                  {isBreak(breakSlot.interviewType) && (
                    <Grid>
                      <BreakIcon />
                    </Grid>
                  )}
                  {isDayBreak(breakSlot.interviewType) && (
                    <Grid>
                      <OvernightIcon />
                    </Grid>
                  )}
                </Grid>
              </Grid>
              <Grid>
                <Button
                  variant="text"
                  size="medium"
                  ref={anchorEl}
                  endIcon={<TriangleDownIcon />}
                  onClick={openBreakDurationMenu}
                >
                  {durationString}
                </Button>
                <DurationMenu
                  id="break-slot-duration-menu"
                  open={open}
                  anchorEl={anchorEl}
                  onSelect={handleDurationSelect}
                  onClose={closeBreakDurationMenu}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid>
            <Grid container justifyContent="space-between" wrap="nowrap" spacing={1}>
              <Grid>
                <MoreMenuOptions
                  index={index}
                  jobStageInterview={breakSlot}
                  jobStageInterviewGroups={jobStageInterviewGroups}
                  onMenuSelect={onMenuSelect}
                />
              </Grid>
              <Grid>
                <IconButton onClick={handleDeleteBreakSlot}>
                  <TrashIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </CanDisable>
    </Paper>
  );
};

BreakSlot.fragments = {
  breakSlot: gql`
    ${MoreMenuOptions.fragments.jobStageInterview}
    fragment BreakSlot_breakSlot on JobStageInterview {
      id
      interviewType
      name
      duration
      isLockedOrder
      ...MoreMenuOptions_jobStageInterview
    }
  `,
  jobStageInterviewGroups: gql`
    ${MoreMenuOptions.fragments.jobStageInterviewGroups}
    fragment BreakSlot_jobStageInterviewGroups on JobStageInterviewGroup {
      id
      ...MoreMenuOptions_jobStageInterviewGroups
    }
  `,
};

export default BreakSlot;
